import Modal from './Modal';
import { Button } from './Button';
import { AiOutlineClose, AiOutlineCheck, AiOutlineReload, AiOutlineUpload } from 'react-icons/ai';
import api from '../api/api';
import { toast } from 'react-toastify';
import { useEffect, useState, useRef } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import { knowAcces } from '../utils/utils';
import useSyncLogs from '../hooks/useSyncLogs';

//3 CASES
//1:Initial State : 0  => User requests update => inital state 1 => update ends => inital state 0
//2:Initial State : 1  => User requests update => warning message
//3:Initial State : 0  => Two or more separate users requests update => The fatser one arrives the other is ejected => update ends => inital state 0

const DataSyncModal = ({ isOpen, closeButton }) => {
	const [newPayrollPreviewAvailable, setNewPayrollPreviewAvailable] = useState(false);
	const [isUpdating, setisUpdating] = useState(false);
	const [lastUpdate, setLastUpdate] = useState(null);
	const [syncStatus, setSyncStatus] = useState({
		currentStep: 'In attesa',
		progress: 0,
		error: null
	});
	const [syncMode, setSyncMode] = useState('latest'); // 'latest' | 'custom'
	const [monthFrom, setMonthFrom] = useState(''); // 'YYYY-MM'
	const [monthTo, setMonthTo] = useState(''); // 'YYYY-MM'
	const [dryRunMonth, setDryRunMonth] = useState(() => {
		const previousMonth = new Date();
		previousMonth.setMonth(previousMonth.getMonth() - 1);
		return previousMonth.toISOString().slice(0, 7);
	});
	const [dryRunLoading, setDryRunLoading] = useState(false);
	const [dryRunReport, setDryRunReport] = useState(null);
	const [dryRunDetailRow, setDryRunDetailRow] = useState(null);
	const [dryRunCompanySearch, setDryRunCompanySearch] = useState('');
	const dryRunTargetOptions = [
		{ id: 'millpart:001', label: 'Millpart — 001' },
		{ id: 'miller:001', label: 'Miller — 001' },
		{ id: 'miller:002', label: 'Miller — 002' },
		{ id: 'brusa:001', label: 'Brusa — 001' },
		{ id: 'sodalis:001', label: 'Sodalis — 001' },
	];
	const [dryRunTargets, setDryRunTargets] = useState(['miller:001', 'miller:002']);
	const [excelFile, setExcelFile] = useState(null);
	const [previewF24Data, setPreviewF24Data] = useState(null);
	const [previewF24Loading, setPreviewF24Loading] = useState(false);
	const [executeF24Loading, setExecuteF24Loading] = useState(false);

	// Cleanup elaborazioni states
	const [cleanupPreviewData, setCleanupPreviewData] = useState(null);
	const [cleanupPreviewLoading, setCleanupPreviewLoading] = useState(false);
	const [cleanupExecuteLoading, setCleanupExecuteLoading] = useState(false);

	// Import elaborazioni states
	const [executeElaborazioniLoading, setExecuteElaborazioniLoading] = useState(false);

	// Reset all passwords states
	const [resetPasswordsLoading, setResetPasswordsLoading] = useState(false);

	// WebSocket logs
	const { logs: syncLogs, isConnected: wsConnected, clearLogs } = useSyncLogs(isUpdating && isOpen);
	const consoleEndRef = useRef(null);

	// Historical logs
	const [historicalLogs, setHistoricalLogs] = useState([]);
	const [logFilter, setLogFilter] = useState('all'); // 'all', 'warn', 'error', 'warn,error'
	const [logsPage, setLogsPage] = useState(0);
	const [logsTotal, setLogsTotal] = useState(0);
	const [logsLoading, setLogsLoading] = useState(false);
	const logsPerPage = 50;

	useEffect(() => {
		checkState();
		if (isOpen && !isUpdating) {
			clearLogs(); // Clear logs when modal opens and not syncing
			fetchHistoricalLogs(); // Load historical logs when modal opens
		}
	}, [isOpen, isUpdating]);

	// Fetch historical logs when filter or page changes
	useEffect(() => {
		if (isOpen && !isUpdating) {
			fetchHistoricalLogs();
		}
	}, [logFilter, logsPage]);

	const fetchHistoricalLogs = async () => {
		try {
			setLogsLoading(true);
			const offset = logsPage * logsPerPage;
			const levelParam = logFilter === 'all' ? '' : `&level=${logFilter}`;
			const response = await api.get(`sync/logs?limit=${logsPerPage}&offset=${offset}${levelParam}`);

			setHistoricalLogs(response.data.logs);
			setLogsTotal(response.data.total);
		} catch (error) {
			console.error('Error fetching historical logs:', error);
			toast.error('Errore nel caricamento dei log storici');
		} finally {
			setLogsLoading(false);
		}
	};

	// Auto-scroll to bottom when new logs arrive
	useEffect(() => {
		if (consoleEndRef.current && syncLogs.length > 0) {
			consoleEndRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [syncLogs]);

	useEffect(() => {
		let intervalId;

		if (isUpdating && isOpen) {
			intervalId = setInterval(async () => {
				try {
					const result = await api.get('sync/status');
					setSyncStatus({
						currentStep: result.data.currentStep,
						progress: result.data.progress,
						error: result.data.error
					});

					if (result.data.progress >= 100 || !result.data.isRunning) {
						setisUpdating(false);
						clearInterval(intervalId);

						// Mostra messaggio di successo o errore
						if (result.data.error) {
							toast.error(result.data.error);
						} else {
							toast.success('Sincronizzazione completata con successo');
						}
					}
				} catch (error) {
					console.error('Error fetching sync status:', error);
				}
			}, 2000); // Ogni 2 secondi
		}

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
		};
	}, [isUpdating, isOpen]);

	const checkState = async () => {
		const result = await api.get('sync');
		setisUpdating(result.data.state);
		setLastUpdate(result.data.lastUpdate);
		setNewPayrollPreviewAvailable(result.data.newPayrollPreviewAvailable === true);

		if (result.data.state === 1) {
			try {
				const statusResult = await api.get('sync/status');
				setSyncStatus({
					currentStep: statusResult.data.currentStep,
					progress: statusResult.data.progress,
					error: statusResult.data.error
				});
			} catch (error) {
				console.error('Error fetching initial sync status:', error);
			}
		}
	};

	const formatLastUpdate = (dateString) => {
		if (!dateString) return 'Mai eseguita';

		const date = new Date(dateString);
		const now = new Date();
		const diffMs = now - date;
		const diffMins = Math.floor(diffMs / 60000);

		if (diffMins < 60) {
			return diffMins === 0 ? 'Pochi secondi fa' : `${diffMins} ${diffMins === 1 ? 'minuto' : 'minuti'} fa`;
		}

		return date.toLocaleString('it-IT', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const handleSave = async () => {
		try {
			const body = {};
			if (syncMode === 'custom' && monthFrom && monthTo) {
				body.mode = 'custom';
				body.monthFrom = monthFrom;
				body.monthTo = monthTo;
			}
			const result = await api.put('sync', body);
			const { message, status } = result.data;

			if (status === 'success') {
				// La sincronizzazione è partita correttamente
				setisUpdating(true);
				setSyncStatus({
					currentStep: 'Avvio sincronizzazione...',
					progress: 0,
					error: null
				});
				toast.success(message, {
					position: 'top-right',
					autoClose: 3000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					theme: 'light',
				});
			} else if (status === 'info') {
				// Sincronizzazione già in corso
				toast.warn(message, {
					position: 'top-right',
					autoClose: 4000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					theme: 'light',
				});
				// Verifica lo stato corrente
				await checkState();
			} else {
				// Errore
				toast.error(message || 'Errore durante l\'avvio della sincronizzazione', {
					position: 'top-right',
					autoClose: 4000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					theme: 'light',
				});
			}
		} catch (error) {
			toast.error('Errore durante la sincronizzazione', {
				position: 'top-right',
				autoClose: 4000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				theme: 'light',
			});
		}
	};

	const handleNewFlowsDryRun = async () => {
		try {
			setDryRunLoading(true);
			setDryRunReport(null);
			const response = await api.post('sync/dry-run-nuovi-flussi', {
				month: dryRunMonth,
				targets: dryRunTargets,
			});
			setDryRunReport(response.data);
			toast.success('Dry run completato: nessun dato è stato scritto');
		} catch (error) {
			toast.error(error.response?.data?.message || 'Errore durante il dry run dei nuovi flussi');
		} finally {
			setDryRunLoading(false);
		}
	};

	const toggleDryRunTarget = (targetId) => {
		setDryRunTargets((current) => current.includes(targetId)
			? current.filter((id) => id !== targetId)
			: [...current, targetId]);
	};

	const formatDryRunValue = (value) => value === null || value === undefined ? 'N/D' : value;
	const dryRunSourceError = (key) => dryRunReport?.sources
		.find((source) => source.key === key)?.endpoints
		.filter((endpoint) => endpoint.status === 'error')
		.map((endpoint) => `${endpoint.site}/${endpoint.company}: ${endpoint.error}`)
		.join(' · ');

	const handleForceReset = async () => {
		try {
			await api.post('sync/reset');
			toast.success('Sincronizzazione resettata con successo', {
				position: 'top-right',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				theme: 'light',
			});
			// Ricontrolla lo stato dopo il reset
			await checkState();
		} catch (error) {
			toast.error('Errore nel reset della sincronizzazione', {
				position: 'top-right',
				autoClose: 4000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				theme: 'light',
			});
		}
	};

	const handlePreviewF24 = async () => {
		if (!excelFile) {
			toast.error("Seleziona un file Excel prima di procedere");
			return;
		}

		if (!knowAcces(["admin", "team_leader"])) {
			toast.error("Non hai i permessi per eseguire questa operazione");
			return;
		}

		try {
			setPreviewF24Loading(true);
			setPreviewF24Data(null);

			const formData = new FormData();
			formData.append('file', excelFile);

			const response = await api.post("/elaborazioni/import-excel/preview-f24", formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});

			if (response.data.success) {
				setPreviewF24Data(response.data.data);
				toast.success("Preview generata con successo - Tutte le righe", {
					position: 'top-right',
					autoClose: 3000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					theme: 'light',
				});
			} else {
				toast.error(response.data.message || "Errore durante la generazione della preview", {
					position: 'top-right',
					autoClose: 4000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					theme: 'light',
				});
			}
		} catch (error) {
			console.error("Errore durante la preview:", error);
			toast.error(error.response?.data?.message || "Errore durante la generazione della preview F24", {
				position: 'top-right',
				autoClose: 4000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				theme: 'light',
			});
		} finally {
			setPreviewF24Loading(false);
		}
	};

	const handleExecuteF24 = async () => {
		if (!excelFile) {
			toast.error("Seleziona un file Excel prima di procedere");
			return;
		}

		if (!knowAcces(["admin", "team_leader"])) {
			toast.error("Non hai i permessi per eseguire questa operazione");
			return;
		}

		const confirmImport = window.confirm("Sei sicuro di voler eseguire l'import F24? Questa operazione modificherà il database con tutte le righe del foglio F24.");
		if (!confirmImport) return;

		try {
			setExecuteF24Loading(true);

			const formData = new FormData();
			formData.append('file', excelFile);

			const response = await api.post("/elaborazioni/import-excel/execute-f24", formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});

			if (response.data.success) {
				const result = response.data.data;
				toast.success(`Import completato! ${result.processedRows} righe processate. Inseriti: ${result.inserted.incarichi} incarichi, ${result.inserted.referenti} referenti, ${result.inserted.iban} IBAN. Aggiornati: ${result.updated.incarichi} incarichi.`, {
					position: 'top-right',
					autoClose: 8000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					theme: 'light',
				});

				// Reset dopo import
				setPreviewF24Data(null);

				if (result.errors && result.errors.length > 0) {
					console.warn("Errori durante l'import:", result.errors);
					toast.warn(`Attenzione: ${result.errors.length} errori durante l'import. Controlla la console per i dettagli.`, {
						position: 'top-right',
						autoClose: 5000,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: true,
						draggable: true,
						theme: 'light',
					});
				}
			} else {
				toast.error(response.data.message || "Errore durante l'import", {
					position: 'top-right',
					autoClose: 4000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					theme: 'light',
				});
			}
		} catch (error) {
			console.error("Errore durante l'import F24:", error);
			toast.error(error.response?.data?.message || "Errore durante l'esecuzione dell'import F24", {
				position: 'top-right',
				autoClose: 4000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				theme: 'light',
			});
		} finally {
			setExecuteF24Loading(false);
		}
	};

	const handleCleanupPreview = async () => {
		if (!knowAcces(["admin", "team_leader"])) {
			toast.error("Non hai i permessi per eseguire questa operazione");
			return;
		}

		try {
			setCleanupPreviewLoading(true);
			setCleanupPreviewData(null);

			const response = await api.get("/elaborazioni/cleanup/preview");

			if (response.data.success) {
				setCleanupPreviewData(response.data.data);
				toast.success("Preview cleanup generata con successo", {
					position: 'top-right',
					autoClose: 3000,
					theme: 'light',
				});
			} else {
				toast.error(response.data.message || "Errore durante la preview del cleanup", {
					position: 'top-right',
					autoClose: 4000,
					theme: 'light',
				});
			}
		} catch (error) {
			console.error("Errore durante la preview cleanup:", error);
			toast.error(error.response?.data?.message || "Errore durante la preview del cleanup", {
				position: 'top-right',
				autoClose: 4000,
				theme: 'light',
			});
		} finally {
			setCleanupPreviewLoading(false);
		}
	};

	const handleCleanupExecute = async () => {
		if (!knowAcces(["admin", "team_leader"])) {
			toast.error("Non hai i permessi per eseguire questa operazione");
			return;
		}

		const confirmCleanup = window.confirm(
			"⚠️ ATTENZIONE: Questa operazione cancellerà TUTTI i seguenti dati:\n\n" +
			"- Tutti gli incarichi F24 (data inizio/fine)\n" +
			"- Tutte le email referenti con flag Quietanza F24\n" +
			"- Tutte le email referenti con flag Responsabile Assunzioni\n" +
			"- Tutti gli IBAN\n\n" +
			"Questa operazione è IRREVERSIBILE.\n\n" +
			"Sei sicuro di voler procedere?"
		);
		if (!confirmCleanup) return;

		try {
			setCleanupExecuteLoading(true);

			const response = await api.post("/elaborazioni/cleanup/execute");

			if (response.data.success) {
				const stats = response.data.data;
				toast.success(
					`Cleanup completato! Cancellati: ${stats.f24_incarichi} incarichi F24, ${stats.referenti_quietanza_f24} referenti quietanza, ${stats.referenti_responsabile_assunzioni} referenti assunzioni, ${stats.iban_azienda} IBAN.`,
					{
						position: 'top-right',
						autoClose: 8000,
						theme: 'light',
					}
				);

				// Reset preview
				setCleanupPreviewData(null);
			} else {
				toast.error(response.data.message || "Errore durante il cleanup", {
					position: 'top-right',
					autoClose: 4000,
					theme: 'light',
				});
			}
		} catch (error) {
			console.error("Errore durante il cleanup:", error);
			toast.error(error.response?.data?.message || "Errore durante l'esecuzione del cleanup", {
				position: 'top-right',
				autoClose: 4000,
				theme: 'light',
			});
		} finally {
			setCleanupExecuteLoading(false);
		}
	};

	const handleExecuteElaborazioni = async () => {
		if (!excelFile) {
			toast.error("Seleziona un file Excel prima di procedere");
			return;
		}

		if (!knowAcces(["admin", "team_leader"])) {
			toast.error("Non hai i permessi per eseguire questa operazione");
			return;
		}

		const confirmImport = window.confirm("Sei sicuro di voler eseguire l'import ELABORAZIONI? Questa operazione importerà le email dei referenti responsabili assunzioni.");
		if (!confirmImport) return;

		try {
			setExecuteElaborazioniLoading(true);

			const formData = new FormData();
			formData.append('file', excelFile);

			const response = await api.post("/elaborazioni/import-excel/execute-elaborazioni", formData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				}
			});

			if (response.data.success) {
				const result = response.data.data;
				toast.success(
					`Import ELABORAZIONI completato! ${result.processedRows} righe processate. Inseriti: ${result.inserted.referenti} referenti.`,
					{
						position: 'top-right',
						autoClose: 8000,
						theme: 'light',
					}
				);

				if (result.errors && result.errors.length > 0) {
					console.warn("Errori durante l'import:", result.errors);
					toast.warn(`Attenzione: ${result.errors.length} errori durante l'import. Controlla la console per i dettagli.`, {
						position: 'top-right',
						autoClose: 5000,
						theme: 'light',
					});
				}
			} else {
				toast.error(response.data.message || "Errore durante l'import ELABORAZIONI", {
					position: 'top-right',
					autoClose: 4000,
					theme: 'light',
				});
			}
		} catch (error) {
			console.error("Errore durante l'import ELABORAZIONI:", error);
			toast.error(error.response?.data?.message || "Errore durante l'esecuzione dell'import ELABORAZIONI", {
				position: 'top-right',
				autoClose: 4000,
				theme: 'light',
			});
		} finally {
			setExecuteElaborazioniLoading(false);
		}
	};

	const handleResetAllPasswords = async () => {
		if (!knowAcces(["admin"])) {
			toast.error("Non hai i permessi per eseguire questa operazione");
			return;
		}

		const confirmReset = window.confirm(
			"⚠️ ATTENZIONE: Questa operazione resetterà le password di TUTTI gli utenti attivi a 'Miller123!'.\n\n" +
			"Questa operazione è IRREVERSIBILE.\n\n" +
			"Sei sicuro di voler procedere?"
		);
		if (!confirmReset) return;

		try {
			setResetPasswordsLoading(true);

			const response = await api.post("/utenti/reset-all-passwords");

			if (response.data.success) {
				toast.success(
					`Reset completato! ${response.data.affectedRows} password resettate a 'Miller123!'`,
					{
						position: 'top-right',
						autoClose: 6000,
						theme: 'light',
					}
				);
			} else {
				toast.error(response.data.message || "Errore durante il reset delle password", {
					position: 'top-right',
					autoClose: 4000,
					theme: 'light',
				});
			}
		} catch (error) {
			console.error("Errore durante il reset delle password:", error);
			toast.error(error.response?.data?.message || "Errore durante l'esecuzione del reset delle password", {
				position: 'top-right',
				autoClose: 4000,
				theme: 'light',
			});
		} finally {
			setResetPasswordsLoading(false);
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			closeButton={closeButton}
				headerText='Sincronizzazione e Manutenzione Dati'
			size='large'
			>
				<Modal isOpen={Boolean(dryRunDetailRow)} closeButton={() => setDryRunDetailRow(null)} headerText={dryRunDetailRow ? `Dettaglio differenze — ${dryRunDetailRow.name || dryRunDetailRow.code}` : 'Dettaglio differenze'} size='compact'>
					{dryRunDetailRow && <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm text-slate-900 bg-white'>
						<div className='rounded-lg border border-amber-300 bg-amber-50 p-4'><div className='font-bold text-lg text-amber-900 mb-3'>Vecchio metodo</div><div className='space-y-2 text-base'><div className='font-bold'>Totale: {dryRunDetailRow.old}</div><div>Chiamata: {dryRunDetailRow.oldBreakdown.onCall || 0}</div><div>Somministrati: {dryRunDetailRow.oldBreakdown.temporary || 0}</div><div>Sportivi: {dryRunDetailRow.oldBreakdown.sports || 0}</div><div>Distaccati: {dryRunDetailRow.oldBreakdown.seconded || 0}</div><div>Liquidazione mensile: {dryRunDetailRow.oldBreakdown.monthly || 0}</div><div>Cedolini separati: {dryRunDetailRow.oldBreakdown.separate || 0}</div></div></div>
						<div className='rounded-lg border border-blue-300 bg-blue-50 p-4'><div className='font-bold text-lg text-blue-900 mb-3'>Riepilogo cedo</div><div className='space-y-2 text-base'><div className='font-bold'>Totale: {formatDryRunValue(dryRunDetailRow.summary)}</div><div>Chiamata: {dryRunDetailRow.newBreakdown?.onCall || 0}</div><div>Somministrati: {dryRunDetailRow.newBreakdown?.temporary || 0}</div><div>Sportivi: {dryRunDetailRow.newBreakdown?.sports || 0}</div><div>Distaccati: {dryRunDetailRow.newBreakdown?.seconded || 0}</div><div>Edili: {dryRunDetailRow.newBreakdown?.construction || 0}</div><div>Distaccati estero: {dryRunDetailRow.newBreakdown?.foreignSecondment || 0}</div><div>Mens. agg. anticipo: {dryRunDetailRow.newBreakdown?.additionalMonthAdvance || 0}</div><div>Mens. agg. saldo: {dryRunDetailRow.newBreakdown?.additionalMonthBalance || 0}</div></div><div className='text-xs font-semibold uppercase tracking-wide text-blue-800 mt-5 mb-1'>Tipi cedolino ({dryRunDetailRow.summaryRows.length})</div><div className='max-h-[25vh] overflow-auto bg-white border border-blue-200 rounded px-3'>{dryRunDetailRow.summaryRows.map((item) => <div key={item.payslipType || 'tipo-assente'} className='border-b border-slate-200 py-2.5 last:border-0'><span className='font-medium'>{item.payslipTypeDescription || `Tipo ${item.payslipType || '—'}`}</span><span className='float-right font-bold'>QTA {item.quantity}</span></div>)}</div></div>
						<div className='rounded-lg border border-emerald-300 bg-emerald-50 p-4'><div className='font-bold text-lg text-emerald-900 mb-3'>Persone cedo_qta <span className='text-sm font-medium'>({dryRunDetailRow.personRows.length})</span></div><div className='max-h-[55vh] overflow-auto bg-white border border-emerald-200 rounded px-3'>{dryRunDetailRow.personRows.map((item, index) => <div key={index} className='border-b border-slate-200 py-2.5 last:border-0'><div className='font-bold text-slate-900'>{item.employee || 'Identificativo assente'} <span className='float-right'>QTA {item.quantity}</span></div><div className='text-slate-700 mt-0.5'>{item.payslipTypeDescription || `Tipo ${item.payslipType || '—'}`}</div></div>)}</div></div>
					</div>}
				</Modal>
				{!isUpdating ? (
				<div className='flex flex-col my-4 space-y-6'>
					{/* SEZIONE 1: Sincronizzazione Dati */}
					<div className='border border-gray-200 rounded-lg p-4'>
						<h3 className='text-lg font-semibold text-miller-300 dark:text-slate-100 mb-3'>
							Sincronizzazione Dati da Zucchetti
						</h3>

						<div className='mb-4'>
							<p className='text-sm text-gray-600 dark:text-slate-400 mb-3'>
								Importa tutti i dati aggiornati dai sistemi Zucchetti (aziende, cedolini, referenti, CCNL, ecc.)
							</p>

							{/* Data ultima sincronizzazione */}
							<div className='text-sm text-gray-500 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded border border-gray-200 dark:border-gray-600'>
								<span className='font-medium'>Ultima sincronizzazione:</span>{' '}
								<span className='text-gray-700 dark:text-slate-300'>{formatLastUpdate(lastUpdate)}</span>
							</div>
						</div>

						{/* Selezione modalità import cedolini */}
						<div className='mb-4'>
							<p className='text-sm font-medium text-gray-700 dark:text-slate-300 mb-2'>
								Modalità import cedolini:
							</p>
							<div className='space-y-2'>
								<label className='flex items-center gap-2 text-sm cursor-pointer text-gray-600 dark:text-slate-400'>
									<input
										type='radio'
										name='syncMode'
										checked={syncMode === 'latest'}
										onChange={() => setSyncMode('latest')}
										className='accent-miller-300'
									/>
									Importa ultimo mese (default)
								</label>
								<label className='flex items-center gap-2 text-sm cursor-pointer text-gray-600 dark:text-slate-400'>
									<input
										type='radio'
										name='syncMode'
										checked={syncMode === 'custom'}
										onChange={() => setSyncMode('custom')}
										className='accent-miller-300'
									/>
									Importa periodo specifico
								</label>
								{syncMode === 'custom' && (
									<div className='ml-6 flex items-center gap-2'>
										<div>
											<label className='block text-xs text-gray-500 dark:text-slate-500 mb-1'>Da</label>
											<input
												type='month'
												value={monthFrom}
												onChange={(e) => {
													setMonthFrom(e.target.value);
													if (monthTo && e.target.value > monthTo) setMonthTo(e.target.value);
												}}
												max={new Date().toISOString().slice(0, 7)}
												className='text-sm border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-slate-300'
											/>
										</div>
										<div>
											<label className='block text-xs text-gray-500 dark:text-slate-500 mb-1'>A</label>
											<input
												type='month'
												value={monthTo}
												onChange={(e) => setMonthTo(e.target.value)}
												min={monthFrom || undefined}
												max={new Date().toISOString().slice(0, 7)}
												className='text-sm border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-slate-300'
											/>
										</div>
									</div>
								)}
							</div>
						</div>

						<Button
							className={`success-button w-full ${syncMode === 'custom' && (!monthFrom || !monthTo) ? 'opacity-50 cursor-not-allowed' : ''}`}
							size='md'
							text={'Avvia Sincronizzazione'}
							onClick={handleSave}
							disabled={syncMode === 'custom' && (!monthFrom || !monthTo)}
							icon={<AiOutlineCheck size={20} />}
						/>
					</div>

					{false && newPayrollPreviewAvailable && knowAcces(["admin", "parc_admin"]) && (
						<div className='border border-amber-300 bg-amber-50 rounded-lg p-5 text-slate-800'>
							<h3 className='text-xl font-semibold text-amber-800 mb-2'>
								Anteprima nuovi flussi Zucchetti
							</h3>
							<p className='text-sm text-slate-700 mb-4'>
								Legge cedolini, dettagli per persona e fondi senza importare o modificare alcun dato.
							</p>
							<div className='mb-3'>
								<div className='text-sm font-medium text-slate-700 mb-2'>Ambienti da interrogare</div>
								<div className='flex flex-wrap gap-x-5 gap-y-2'>
									{dryRunTargetOptions.map((target) => (
										<label key={target.id} className='flex items-center gap-2 text-sm font-medium text-slate-800 cursor-pointer'>
											<input
												type='checkbox'
												checked={dryRunTargets.includes(target.id)}
												onChange={() => toggleDryRunTarget(target.id)}
												className='accent-amber-600'
											/>
											{target.label}
										</label>
									))}
								</div>
							</div>
							<div className='flex flex-wrap items-end gap-3 mb-3'>
								<div>
									<label className='block text-sm font-medium text-slate-700 mb-2'>Mese da verificare</label>
									<input
										type='month'
										value={dryRunMonth}
										onChange={(event) => setDryRunMonth(event.target.value)}
										className='text-sm font-medium border border-slate-400 rounded px-3 py-2 bg-white text-slate-900'
									/>
								</div>
								<Button
									className={`warning-button ${dryRunLoading || !dryRunMonth || dryRunTargets.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
									text={dryRunLoading ? 'Dry run in corso...' : 'Avvia dry run nuovi flussi'}
									onClick={handleNewFlowsDryRun}
									disabled={dryRunLoading || !dryRunMonth || dryRunTargets.length === 0}
									icon={dryRunLoading ? <CircularProgress size={18} /> : <AiOutlineReload size={20} />}
								/>
							</div>

							{dryRunReport && (
								<div className='space-y-4 text-sm text-gray-800 dark:text-slate-100'>
									<h4 className='font-semibold text-base'>Confronto diretto cedolini</h4>
									<div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
										<div className='bg-white dark:bg-slate-800 border rounded p-3'>
											<div className='text-xs text-gray-500 dark:text-slate-400'>Vecchio metodo</div>
											<div className='text-2xl font-semibold'>{dryRunReport.comparison.totals.old}</div>
										</div>
										<div className='bg-white dark:bg-slate-800 border rounded p-3'>
											<div className='text-xs text-gray-500 dark:text-slate-400'>Nuovo riepilogo cedo</div>
											<div className='text-2xl font-semibold'>{formatDryRunValue(dryRunReport.comparison.totals.summary)}</div>
											{dryRunSourceError('cedolini') && <div className='text-red-600'>Non completato: {dryRunSourceError('cedolini')}</div>}
											{dryRunReport.comparison.totals.summary !== null &&
											<div className={dryRunReport.comparison.totals.differenceSummaryVsOld === 0 ? 'text-green-600' : 'text-red-600'}>
												vs vecchio: {dryRunReport.comparison.totals.differenceSummaryVsOld > 0 ? '+' : ''}{dryRunReport.comparison.totals.differenceSummaryVsOld}
											</div>}
										</div>
										<div className='bg-white dark:bg-slate-800 border rounded p-3'>
											<div className='text-xs text-gray-500 dark:text-slate-400'>Nuovo dettaglio cedo_qta</div>
											<div className='text-2xl font-semibold'>{formatDryRunValue(dryRunReport.comparison.totals.detail)}</div>
											{dryRunSourceError('dettagliPersone') && <div className='text-red-600'>Non completato: {dryRunSourceError('dettagliPersone')}</div>}
											{dryRunReport.comparison.totals.detail !== null &&
											<div className={dryRunReport.comparison.totals.differenceDetailVsOld === 0 ? 'text-green-600' : 'text-red-600'}>
												vs vecchio: {dryRunReport.comparison.totals.differenceDetailVsOld > 0 ? '+' : ''}{dryRunReport.comparison.totals.differenceDetailVsOld}
											</div>}
										</div>
									</div>

									<div className='border rounded bg-white dark:bg-slate-800 overflow-hidden'>
										<div className='p-2 border-b bg-gray-50 dark:bg-slate-700'>
											<input
												type='search'
												value={dryRunCompanySearch}
												onChange={(event) => setDryRunCompanySearch(event.target.value)}
												placeholder='Cerca azienda o codice...'
												className='w-full md:w-96 text-sm border border-slate-300 rounded px-3 py-2 bg-white text-slate-900'
											/>
										</div>
										<div className='max-h-96 overflow-auto'>
											<table className='w-full text-xs'>
												<thead className='sticky top-0 bg-gray-100 dark:bg-slate-700'>
													<tr><th className='text-left p-2'>Azienda</th><th className='text-right p-2'>Vecchio</th><th className='text-right p-2'>cedo</th><th className='text-right p-2'>cedo_qta</th><th className='text-right p-2'>Δ cedo</th><th className='text-right p-2'>Δ dettaglio</th></tr>
												</thead>
											<tbody>{[...dryRunReport.comparison.companies]
												.filter((row) => `${row.name || ''} ${row.code || ''}`.toLocaleLowerCase('it').includes(dryRunCompanySearch.trim().toLocaleLowerCase('it')))
												.sort((a, b) => {
													const aHasDifference = (Number.isFinite(a.differenceSummaryVsOld) && a.differenceSummaryVsOld !== 0)
														|| (Number.isFinite(a.differenceDetailVsOld) && a.differenceDetailVsOld !== 0);
													const bHasDifference = (Number.isFinite(b.differenceSummaryVsOld) && b.differenceSummaryVsOld !== 0)
														|| (Number.isFinite(b.differenceDetailVsOld) && b.differenceDetailVsOld !== 0);
													return Number(bHasDifference) - Number(aHasDifference);
												})
												.map((row) => (
											<tr key={row.code} onClick={() => setDryRunDetailRow(row)} className={`border-t cursor-pointer ${((Number.isFinite(row.differenceSummaryVsOld) && row.differenceSummaryVsOld !== 0) || (Number.isFinite(row.differenceDetailVsOld) && row.differenceDetailVsOld !== 0)) ? 'bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50' : 'bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700'}`} title='Clicca per aprire il dettaglio'>
												<td className='p-2'><div className='font-medium'>{row.name || row.code}</div><div className='text-gray-500'>{row.code}</div></td>
														<td className='text-right p-2'>{row.old}</td><td className='text-right p-2'>{formatDryRunValue(row.summary)}</td><td className='text-right p-2'>{formatDryRunValue(row.detail)}</td>
														<td className='text-right p-2'>{row.differenceSummaryVsOld === null ? 'N/D' : `${row.differenceSummaryVsOld > 0 ? '+' : ''}${row.differenceSummaryVsOld}`}</td>
													<td className='text-right p-2'>{row.differenceDetailVsOld === null ? 'N/D' : `${row.differenceDetailVsOld > 0 ? '+' : ''}${row.differenceDetailVsOld}`}</td>
											</tr>
												))}</tbody>
											</table>
										</div>
									</div>
									<div className='text-xs text-gray-500 dark:text-slate-400'>Fondi COVIP: {dryRunReport.sources.find((source) => source.key === 'fondi')?.records || 0} righe · Scritture eseguite: {dryRunReport.writesPerformed}</div>
								</div>
							)}
						</div>
					)}

					{/*/!* SEZIONE 3: Cleanup Campi Import *!/*/}
					{/*<div className='border border-gray-200 rounded-lg p-4'>*/}
					{/*	<h3 className='text-lg font-semibold text-miller-300 dark:text-slate-100 mb-3'>*/}
					{/*		Cleanup Campi Import*/}
					{/*	</h3>*/}

					{/*	<div className='mb-4'>*/}
					{/*		<p className='text-sm text-gray-600 dark:text-slate-400 mb-3'>*/}
					{/*			Cancella tutti i dati attualmente presenti per i campi oggetto dell'import da Excel (incarichi F24, referenti, IBAN).*/}
					{/*		</p>*/}

					{/*		<div className='bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-3 mb-3'>*/}
					{/*			<p className='text-xs text-red-700 dark:text-red-400 mb-2'>*/}
					{/*				<strong>⚠️ ATTENZIONE:</strong> Questa operazione cancellerà TUTTI i seguenti dati:*/}
					{/*			</p>*/}
					{/*			<ul className='text-xs text-red-700 dark:text-red-400 list-disc ml-4 space-y-1'>*/}
					{/*				<li>Tutti gli incarichi F24 (data inizio/fine incarico)</li>*/}
					{/*				<li>Tutte le email referenti con flag Quietanza F24</li>*/}
					{/*				<li>Tutte le email referenti con flag Responsabile Assunzioni</li>*/}
					{/*				<li>Tutti gli IBAN attivi</li>*/}
					{/*			</ul>*/}
					{/*			<p className='text-xs text-red-700 dark:text-red-400 mt-2'>*/}
					{/*				<strong>Questa operazione è IRREVERSIBILE.</strong> Utilizzare solo prima di un import completo da Excel.*/}
					{/*			</p>*/}
					{/*		</div>*/}

					{/*		<div className='bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-3 mb-3'>*/}
					{/*			<p className='text-xs text-blue-700 dark:text-blue-400'>*/}
					{/*				<strong>ℹ️ Quando utilizzare:</strong> Prima di importare un nuovo file Excel completo per evitare dati obsoleti. Il cleanup è opzionale se si vuole solo aggiornare/integrare i dati esistenti.*/}
					{/*			</p>*/}
					{/*		</div>*/}
					{/*	</div>*/}

					{/*	/!* Pulsanti Cleanup *!/*/}
					{/*	<div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-4'>*/}
					{/*		<Button*/}
					{/*			className={`info-button w-full ${cleanupPreviewLoading ? 'opacity-50 cursor-not-allowed' : ''}`}*/}
					{/*			size='md'*/}
					{/*			text={cleanupPreviewLoading ? 'Generazione preview...' : 'Preview Cleanup'}*/}
					{/*			onClick={handleCleanupPreview}*/}
					{/*			disabled={cleanupPreviewLoading}*/}
					{/*			icon={cleanupPreviewLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <AiOutlineCheck size={20} />}*/}
					{/*		/>*/}
					{/*		<Button*/}
					{/*			className={`error-button w-full ${cleanupExecuteLoading ? 'opacity-50 cursor-not-allowed' : ''}`}*/}
					{/*			size='md'*/}
					{/*			text={cleanupExecuteLoading ? 'Cleanup in corso...' : 'Esegui Cleanup'}*/}
					{/*			onClick={handleCleanupExecute}*/}
					{/*			disabled={cleanupExecuteLoading}*/}
					{/*			icon={cleanupExecuteLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <HiOutlineTrash size={20} />}*/}
					{/*		/>*/}
					{/*	</div>*/}

					{/*	/!* Risultati Preview Cleanup *!/*/}
					{/*	{cleanupPreviewData && (*/}
					{/*		<div className='border border-orange-300 dark:border-orange-700 rounded-lg p-4 bg-orange-50 dark:bg-orange-900/20'>*/}
					{/*			<h4 className='text-md font-semibold text-orange-800 dark:text-orange-300 mb-3'>*/}
					{/*				Preview Cleanup - Record da cancellare*/}
					{/*			</h4>*/}

					{/*			<div className='space-y-2'>*/}
					{/*				<div className='flex justify-between items-center text-sm'>*/}
					{/*					<span className='text-gray-700 dark:text-slate-300'>Incarichi F24:</span>*/}
					{/*					<span className='font-semibold text-gray-900 dark:text-slate-100'>{cleanupPreviewData.f24_incarichi}</span>*/}
					{/*				</div>*/}
					{/*				<div className='flex justify-between items-center text-sm'>*/}
					{/*					<span className='text-gray-700 dark:text-slate-300'>Referenti Quietanza F24:</span>*/}
					{/*					<span className='font-semibold text-gray-900 dark:text-slate-100'>{cleanupPreviewData.referenti_quietanza_f24}</span>*/}
					{/*				</div>*/}
					{/*				<div className='flex justify-between items-center text-sm'>*/}
					{/*					<span className='text-gray-700 dark:text-slate-300'>Referenti Responsabili Assunzioni:</span>*/}
					{/*					<span className='font-semibold text-gray-900 dark:text-slate-100'>{cleanupPreviewData.referenti_responsabile_assunzioni}</span>*/}
					{/*				</div>*/}
					{/*				<div className='flex justify-between items-center text-sm'>*/}
					{/*					<span className='text-gray-700 dark:text-slate-300'>IBAN:</span>*/}
					{/*					<span className='font-semibold text-gray-900 dark:text-slate-100'>{cleanupPreviewData.iban_azienda}</span>*/}
					{/*				</div>*/}
					{/*				<div className='border-t border-orange-300 dark:border-orange-700 pt-2 mt-2'>*/}
					{/*					<div className='flex justify-between items-center text-sm font-bold'>*/}
					{/*						<span className='text-gray-800 dark:text-slate-200'>Totale record da cancellare:</span>*/}
					{/*						<span className='text-red-600 dark:text-red-400'>*/}
					{/*							{cleanupPreviewData.f24_incarichi +*/}
					{/*							 cleanupPreviewData.referenti_quietanza_f24 +*/}
					{/*							 cleanupPreviewData.referenti_responsabile_assunzioni +*/}
					{/*							 cleanupPreviewData.iban_azienda}*/}
					{/*						</span>*/}
					{/*					</div>*/}
					{/*				</div>*/}
					{/*			</div>*/}
					{/*		</div>*/}
					{/*	)}*/}
					{/*</div>*/}

					{/* SEZIONE 4: Import Elaborazioni da Excel */}
					{false && (
					<div className='border border-gray-200 rounded-lg p-4'>
						<h3 className='text-lg font-semibold text-miller-300 dark:text-slate-100 mb-3'>
							Import Elaborazioni da Excel
						</h3>

						<div className='mb-4'>
							<p className='text-sm text-gray-600 dark:text-slate-400 mb-3'>
								Carica un file Excel contenente i dati delle elaborazioni mensili (fogli ELABORAZIONI e F24).
							</p>

							<div className='bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 p-3 mb-3'>
								<p className='text-xs text-blue-700 dark:text-blue-400 mb-2'>
									<strong>Formato Excel richiesto:</strong>
								</p>
								<p className='text-xs text-blue-700 dark:text-blue-400 font-mono'>
									COD.AZI, COD. FT, RAGIONE SOCIALE, GRUPPO AZIENDE, REFERENTE PAGHE 01/2026,
									CARICAMENTO PRESENZE, PRESENZE DIFFERITE, SCADENZA PAGHE, STAMPA LUL,
									MEDIA ELAB. MENSILI, MILLER SICUREZZA, CDL DI RIFERIMENTO, REFERENTE ASSUNZIONI,
									SERVIZIO ASSUNZIONI, PUBBL. CCNL MIP, CCNL APPLICATI,
									E-MAIL REFERENTE (FLAG RESPONSABILE ASSUNZIONI), INIZIO SERVIZIO PAGHE,
									FINE SERVIZIO PAGHE, MOTIVO, REFERENTE PAGHE 12/2025
								</p>
							</div>

							{/* File Input */}
							<div className='mb-3'>
								<label className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2'>
									Seleziona file Excel (.xlsx, .xls)
								</label>
								<input
									id='excel-upload-input'
									type='file'
									accept='.xlsx,.xls'
									onChange={(e) => setExcelFile(e.target.files[0])}
									className='block w-full text-sm text-gray-500 dark:text-slate-400
										file:mr-4 file:py-2 file:px-4
										file:rounded file:border-0
										file:text-sm file:font-semibold
										file:bg-miller-300 file:text-white
										hover:file:bg-miller-400
										file:cursor-pointer cursor-pointer
										border border-gray-300 dark:border-gray-600 rounded-lg
										bg-white dark:bg-gray-800'
								/>
								{excelFile && (
									<p className='mt-2 text-sm text-gray-600 dark:text-slate-400'>
										File selezionato: <span className='font-medium'>{excelFile.name}</span>
									</p>
								)}
							</div>
						</div>

						{/* Pulsanti Azioni */}
						<div className='space-y-3'>
							{/* Import ELABORAZIONI */}
							<div>
								<h4 className='text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2'>
									Foglio ELABORAZIONI (Email Responsabile Assunzioni)
								</h4>
								<Button
									className={`success-button w-full ${(!excelFile || executeElaborazioniLoading) ? 'opacity-50 cursor-not-allowed' : ''}`}
									size='md'
									text={executeElaborazioniLoading ? 'Import in corso...' : 'Esegui Import ELABORAZIONI'}
									onClick={handleExecuteElaborazioni}
									disabled={executeElaborazioniLoading || !excelFile}
									icon={executeElaborazioniLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <AiOutlineUpload size={20} />}
								/>
							</div>

							{/* Import F24 */}
							<div>
								<h4 className='text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2'>
									Foglio F24 (Incarichi, Email Quietanza, IBAN)
								</h4>
								<div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
									<Button
										className={`info-button w-full ${(!excelFile || previewF24Loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
										size='md'
										text={previewF24Loading ? 'Generazione preview...' : 'Preview F24'}
										onClick={handlePreviewF24}
										disabled={previewF24Loading || !excelFile}
										icon={previewF24Loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <AiOutlineCheck size={20} />}
									/>
									<Button
										className={`warning-button w-full ${(!excelFile || executeF24Loading) ? 'opacity-50 cursor-not-allowed' : ''}`}
										size='md'
										text={executeF24Loading ? 'Import in corso...' : 'Esegui Import F24'}
										onClick={handleExecuteF24}
										disabled={executeF24Loading || !excelFile}
										icon={executeF24Loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <AiOutlineUpload size={20} />}
									/>
								</div>
							</div>
						</div>

						{/* Risultati Preview F24 */}
						{previewF24Data && (
							<div className='border border-blue-300 dark:border-blue-700 rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20'>
								<h4 className='text-md font-semibold text-blue-800 dark:text-blue-300 mb-3'>
									Preview Import F24 - {previewF24Data.processedRows} righe totali
								</h4>

								<div className='space-y-3 max-h-96 overflow-y-auto'>
									{previewF24Data.operations.map((op, idx) => (
										<div key={idx} className='border border-gray-200 dark:border-gray-600 rounded p-3 bg-white dark:bg-gray-800'>
											<div className='flex justify-between items-start mb-2'>
												<div>
													<p className='text-sm font-semibold text-gray-800 dark:text-slate-200'>
														Riga {op.row}: {op.codice_azienda} - {op.ragione_sociale}
													</p>
													<p className={`text-xs font-medium ${
														op.status === 'TO_PROCESS' ? 'text-green-600 dark:text-green-400' :
														op.status === 'SKIPPED' ? 'text-red-600 dark:text-red-400' :
														'text-gray-500 dark:text-gray-400'
													}`}>
														{op.status === 'TO_PROCESS' ? '✓ Da processare' :
														 op.status === 'SKIPPED' ? `✗ Saltata: ${op.reason}` :
														 '○ Nessun dato'}
													</p>
												</div>
											</div>

											{op.actions && op.actions.length > 0 && (
												<div className='space-y-2 mt-2'>
													{op.actions.map((action, actionIdx) => (
														<div key={actionIdx} className='text-xs bg-gray-50 dark:bg-gray-700 p-2 rounded'>
															<div className='flex items-center justify-between mb-1'>
																<span className='font-semibold text-gray-700 dark:text-slate-300'>
																	{action.type}
																</span>
																<span className={`px-2 py-0.5 rounded text-xs font-semibold ${
																	action.action === 'INSERT' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
																	'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
																}`}>
																	{action.action}
																</span>
															</div>

															{action.current !== null && action.current !== undefined && (
																<div className='mb-1'>
																	<span className='text-gray-600 dark:text-gray-400'>Attuale: </span>
																	<span className='font-mono text-gray-800 dark:text-slate-200'>
																		{Array.isArray(action.current) ? action.current.join(', ') : JSON.stringify(action.current)}
																	</span>
																</div>
															)}

															{action.new !== null && action.new !== undefined && (
																<div>
																	<span className='text-gray-600 dark:text-gray-400'>Nuovo: </span>
																	<span className='font-mono text-gray-800 dark:text-slate-200'>
																		{Array.isArray(action.new) ? action.new.join(', ') : JSON.stringify(action.new)}
																	</span>
																</div>
															)}

															{action.count && (
																<div className='text-gray-600 dark:text-gray-400 mt-1'>
																	({action.count} {action.count === 1 ? 'elemento' : 'elementi'})
																</div>
															)}
														</div>
													))}
												</div>
											)}
										</div>
									))}
								</div>
							</div>
						)}
					</div>
					)}

					{/* SEZIONE 5: Reset Password Utenti */}
					{/*<div className='border border-gray-200 rounded-lg p-4'>*/}
					{/*	<h3 className='text-lg font-semibold text-miller-300 dark:text-slate-100 mb-3'>*/}
					{/*		Reset Password Utenti*/}
					{/*	</h3>*/}

					{/*	<div className='mb-4'>*/}
					{/*		<p className='text-sm text-gray-600 dark:text-slate-400 mb-3'>*/}
					{/*			Resetta le password di tutti gli utenti attivi alla password di default.*/}
					{/*		</p>*/}

					{/*		<div className='bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-3 mb-3'>*/}
					{/*			<p className='text-xs text-red-700 dark:text-red-400 mb-2'>*/}
					{/*				<strong>⚠️ ATTENZIONE:</strong> Questa operazione resetterà le password di TUTTI gli utenti attivi a:*/}
					{/*			</p>*/}
					{/*			<p className='text-xs text-red-700 dark:text-red-400 font-mono ml-4 mb-2'>*/}
					{/*				Miller123!*/}
					{/*			</p>*/}
					{/*			<p className='text-xs text-red-700 dark:text-red-400'>*/}
					{/*				<strong>Questa operazione è IRREVERSIBILE.</strong> Tutti gli utenti dovranno utilizzare questa password per accedere.*/}
					{/*			</p>*/}
					{/*		</div>*/}

					{/*		<div className='bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-3 mb-3'>*/}
					{/*			<p className='text-xs text-yellow-700 dark:text-yellow-400'>*/}
					{/*				<strong>ℹ️ Quando utilizzare:</strong> Solo in caso di necessità operative o durante la configurazione iniziale del sistema. Utilizzare solo se autorizzati dall'amministratore.*/}
					{/*			</p>*/}
					{/*		</div>*/}
					{/*	</div>*/}

					{/*	<Button*/}
					{/*		className={`error-button w-full ${resetPasswordsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}*/}
					{/*		size='md'*/}
					{/*		text={resetPasswordsLoading ? 'Reset in corso...' : 'Resetta Tutte le Password'}*/}
					{/*		onClick={handleResetAllPasswords}*/}
					{/*		disabled={resetPasswordsLoading}*/}
					{/*		icon={resetPasswordsLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <AiOutlineReload size={20} />}*/}
					{/*	/>*/}
					{/*</div>*/}

					{/* SEZIONE 6: Log Storici */}
					<div className='border border-gray-200 rounded-lg p-4'>
						<div className='flex justify-between items-center mb-3'>
							<h3 className='text-lg font-semibold text-miller-300 dark:text-slate-100'>
								Log Sincronizzazioni
							</h3>
							<div className='flex items-center space-x-2'>
								<label className='text-sm text-gray-600 dark:text-slate-400'>Filtro:</label>
								<select
									value={logFilter}
									onChange={(e) => {
										setLogFilter(e.target.value);
										setLogsPage(0); // Reset to first page on filter change
									}}
									className='text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-slate-300'
								>
									<option value="all">Tutti i log</option>
									<option value="warn,error">Solo warn + error</option>
									<option value="warn">Solo warn</option>
									<option value="error">Solo error</option>
								</select>
							</div>
						</div>

						{logsLoading ? (
							<div className='text-center py-8 text-gray-500'>
								Caricamento log...
							</div>
						) : historicalLogs.length === 0 ? (
							<div className='text-center py-8 text-gray-500'>
								Nessun log disponibile
							</div>
						) : (
							<>
								<div className='border border-gray-300 dark:border-gray-600 rounded-lg max-h-96 overflow-y-auto'>
									<table className='w-full text-sm'>
										<thead className='bg-gray-100 dark:bg-gray-800 sticky top-0'>
											<tr>
												<th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-slate-300'>Data/Ora</th>
												<th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-slate-300'>Livello</th>
												<th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-slate-300'>Step</th>
												<th className='px-3 py-2 text-left text-xs font-semibold text-gray-700 dark:text-slate-300'>Messaggio</th>
														</tr>
										</thead>
										<tbody className='divide-y divide-gray-200 dark:divide-gray-700'>
											{historicalLogs.map((log) => {
												const levelStyles = {
													info: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
													warn: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
													error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
													debug: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
													success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
												};

												const levelStyle = levelStyles[log.level] || 'bg-gray-100 text-gray-800';
												const timestamp = new Date(log.timestamp).toLocaleString('it-IT');

												return (
													<tr key={log.id} className='hover:bg-gray-50 dark:hover:bg-gray-800/50'>
														<td className='px-3 py-2 text-xs text-gray-600 dark:text-slate-400 whitespace-nowrap'>
															{timestamp}
														</td>
														<td className='px-3 py-2'>
															<span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${levelStyle}`}>
																{log.level.toUpperCase()}
															</span>
														</td>
														<td className='px-3 py-2 text-xs text-gray-700 dark:text-slate-300'>
															{log.step || '-'}
														</td>
														<td className='px-3 py-2 text-xs text-gray-700 dark:text-slate-300'>
															<div className='max-w-md truncate' title={log.message}>
																{log.message}
															</div>
															{log.details && (
																<details className='mt-1'>
																	<summary className='cursor-pointer text-blue-600 dark:text-blue-400 hover:underline'>
																		Dettagli
																	</summary>
																	<pre className='mt-2 p-2 bg-gray-100 dark:bg-gray-900 rounded text-xs overflow-x-auto'>
																		{JSON.stringify(log.details, null, 2)}
																	</pre>
																</details>
															)}
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>

								{/* Pagination */}
								<div className='flex justify-between items-center mt-3 text-sm'>
									<div className='text-gray-600 dark:text-slate-400'>
										Visualizzati {logsPage * logsPerPage + 1}-{Math.min((logsPage + 1) * logsPerPage, logsTotal)} di {logsTotal} log
									</div>
									<div className='flex space-x-2'>
										<button
											onClick={() => setLogsPage(Math.max(0, logsPage - 1))}
											disabled={logsPage === 0}
											className={`px-3 py-1 rounded border ${
												logsPage === 0
													? 'bg-gray-100 text-gray-400 cursor-not-allowed'
													: 'bg-white dark:bg-gray-800 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-gray-700'
											} border-gray-300 dark:border-gray-600`}
										>
											Precedente
										</button>
										<button
											onClick={() => setLogsPage(logsPage + 1)}
											disabled={(logsPage + 1) * logsPerPage >= logsTotal}
											className={`px-3 py-1 rounded border ${
												(logsPage + 1) * logsPerPage >= logsTotal
													? 'bg-gray-100 text-gray-400 cursor-not-allowed'
													: 'bg-white dark:bg-gray-800 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-gray-700'
											} border-gray-300 dark:border-gray-600`}
										>
											Successiva
										</button>
									</div>
								</div>
							</>
						)}
					</div>
				</div>
			) : (
				<div className='flex flex-col my-4 space-y-4'>
					{/* Header */}
					<div className='text-center'>
						<h2 className='text-xl font-semibold text-miller-300 dark:text-slate-100'>
							Sincronizzazione in Corso
						</h2>
					</div>

					{/* Header con stato e progresso */}
					<div className='space-y-3'>
						<div className='text-center font-medium text-gray-700 dark:text-slate-300'>
							Sincronizzazione in corso...
						</div>

						{/* Stato corrente */}
						<div className='text-center text-sm text-gray-600 dark:text-slate-400'>
							{syncStatus.currentStep}
						</div>

						{/* Barra di progresso con percentuale */}
						<div className='w-full'>
							<div className='flex justify-between text-xs text-gray-500 mb-1'>
								<span>Progresso</span>
								<span>{syncStatus.progress}%</span>
							</div>
							<LinearProgress
								variant="determinate"
								value={syncStatus.progress}
								sx={{
									height: 8,
									borderRadius: 4,
									'& .MuiLinearProgress-bar': {
										borderRadius: 4,
									}
								}}
							/>
						</div>

						{/* WebSocket status indicator */}
						<div className='flex items-center justify-center text-xs text-gray-500'>
							<div className={`w-2 h-2 rounded-full mr-2 ${wsConnected ? 'bg-green-500' : 'bg-red-500'}`} />
							{wsConnected ? 'Console connessa' : 'Connessione console...'}
						</div>
					</div>

					{/* Console Logs */}
					<div className='border border-gray-200 dark:border-gray-700 rounded-lg'>
						<div className='bg-gray-800 text-gray-100 px-4 py-2 rounded-t-lg flex items-center justify-between'>
							<span className='text-sm font-mono font-semibold'>Console Log</span>
							<span className='text-xs text-gray-400'>{syncLogs.length} messaggi</span>
						</div>

						<div className='bg-gray-900 text-gray-100 p-4 rounded-b-lg max-h-96 overflow-y-auto font-mono text-xs'>
							{syncLogs.length === 0 ? (
								<div className='text-gray-500 text-center py-8'>
									In attesa di log...
								</div>
							) : (
								<div className='space-y-1'>
									{syncLogs.map((log, index) => {
										const levelColors = {
											info: 'text-blue-400',
											warn: 'text-yellow-400',
											error: 'text-red-400',
											debug: 'text-purple-400',
											success: 'text-green-400'
										};

										const color = levelColors[log.level] || 'text-gray-300';
										const timestamp = new Date(log.timestamp).toLocaleTimeString('it-IT');

										return (
											<div key={`${log.id}-${index}`} className='flex items-start space-x-2'>
												<span className='text-gray-600 flex-shrink-0'>{timestamp}</span>
												<span className={`${color} flex-shrink-0 font-semibold`}>[{log.level.toUpperCase()}]</span>
												<span className='text-gray-200 break-all'>{log.message}</span>
											</div>
										);
									})}
									<div ref={consoleEndRef} />
								</div>
							)}
						</div>
					</div>

					{/* Messaggio di errore se presente */}
					{syncStatus.error && (
						<div className='bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-3'>
							<div className='text-red-700 dark:text-red-400 text-sm'>
								<strong>Errore:</strong> {syncStatus.error}
							</div>
						</div>
					)}

					<div className='text-center text-xs text-gray-400'>
						Non chiudere questa finestra durante la sincronizzazione
					</div>

					{/* Bottoni azioni */}
					<div className='flex items-center justify-between pt-4 border-t border-gray-200'>
						<Button
							className='error-button'
							size='md'
							text={'Esci'}
							onClick={closeButton}
							icon={<AiOutlineClose size={20} />}
						/>
						<div className='flex space-x-2'>
							<Button
								className='warning-button'
								size='md'
								text={'Forza Reset'}
								onClick={handleForceReset}
								icon={<AiOutlineReload size={20} />}
							/>
						</div>
					</div>
				</div>
			)}
		</Modal>
	);
};

export default DataSyncModal;
